import { createContext, useCallback, useContext, useState } from 'react';
import PropTypes from 'prop-types';

import { DESIGN_REMIX_STATUS } from 'utlis/constants';

/**
 * Shares prototype generation state between DesignInspirationPanel (writer)
 * and ImageCarousel (reader).
 */
export const PrototypePreviewContext = createContext(null);

export const PrototypePreviewProvider = ({ children }) => {
  const [prototypeMap, setPrototypeMap] = useState({});

  const registerPrototype = useCallback((insightUuid, state) => {
    if (!insightUuid) {
      return;
    }

    setPrototypeMap(prev => {
      if (!state) {
        const next = { ...prev };

        delete next[insightUuid];

        return next;
      }

      return { ...prev, [insightUuid]: { insightUuid, ...state } };
    });
  }, []);

  const clearPrototype = useCallback(insightUuid => {
    if (insightUuid) {
      setPrototypeMap(prev => {
        const next = { ...prev };

        delete next[insightUuid];

        return next;
      });
    }
    else {
      setPrototypeMap({});
    }
  }, []);

  // Returns the prototype for a given page (1-indexed), scoped to a specific insightUuid when provided.
  const getPrototypeForPage = useCallback((page, insightUuid) => {
    const entries = Object.values(prototypeMap).filter(e => {
      if (e.page !== page) {
        return false;
      }

      if (insightUuid != null) {
        return String(e.insightUuid) === String(insightUuid);
      }

      return true;
    });

    if (entries.length === 0) {
      return null;
    }

    return (
      entries.find(e => e.status === DESIGN_REMIX_STATUS.IN_PROGRESS)
      ?? entries.find(e => e.status === DESIGN_REMIX_STATUS.COMPLETED)
      ?? entries.find(e => Array.isArray(e.panels) && e.panels.length > 0)
      ?? null
    );
  }, [prototypeMap]);

  // Returns the ordered array of varient for a given page, scoped to a specific insightUuid when provided.
  const getPanelsForPage = useCallback((page, insightUuid) => {
    const entry = Object.values(prototypeMap).find(e => {
      if (e.page !== page) {
        return false;
      }

      if (insightUuid != null) {
        return String(e.insightUuid) === String(insightUuid);
      }

      return true;
    });

    return entry?.panels ?? [];
  }, [prototypeMap]);

  return (
    <PrototypePreviewContext.Provider
      value={{ prototypeMap, registerPrototype, clearPrototype, getPrototypeForPage, getPanelsForPage }}
    >
      {children}
    </PrototypePreviewContext.Provider>
  );
};

PrototypePreviewProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const usePrototypePreview = () => {
  const ctx = useContext(PrototypePreviewContext);

  return ctx ?? {
    prototypeMap: {},
    registerPrototype: () => {},
    clearPrototype: () => {},
    getPrototypeForPage: () => null,
    getPanelsForPage: () => [],
  };
};
